<template>
  <div class="sp">
    <div class="sp-section-head">
      <h2>Payment Options</h2>
      <p class="sp-help">
        Practice-wide payment options shown to patients when they review their plan.
        TCs can adjust discount or terms per-patient in the Builder. Disabled options
        stay configured but don't show on patient views until re-enabled.
      </p>
    </div>

    <div class="sp-card">
      <h3 class="sp-card-title">Patient call-to-action</h3>
      <div class="sp-field">
        <label>Button language</label>
        <input v-model="local.buttonLanguage" class="sp-input" placeholder="I'm interested" />
        <p class="sp-hint">
          Text on the button patients click to express interest in an option. Examples:
          "I'm interested", "Choose this option", "Tell me more".
        </p>
      </div>
      <div class="sp-field">
        <label>Helper note</label>
        <textarea
          v-model="local.helperNote"
          class="sp-input sp-textarea"
          rows="2"
          placeholder="Optional helper text shown above payment cards on the patient view"
        ></textarea>
      </div>
    </div>

    <!-- Cherry connection. Sits ABOVE the options because "which lender backs this
         option" can only be answered once an account is attached. Hidden entirely when
         the site isn't entitled — cherry-service default-denies server-side regardless,
         so this is presentation, not the gate. -->
    <CherryConnectCard
      v-if="cherryEnabled && siteID"
      :site-id="siteID"
      @change="onCherryChange"
    />

    <!-- Cherry just stopped being usable (disconnected / location removed) and options that
         were backed by it were switched back to None in the form below. Nothing is saved
         until Save - say so, or the office assumes it already took effect. -->
    <div v-if="cherryNotice" class="sp-notice" role="status">
      <i class="fa fa-info-circle"></i> {{ cherryNotice }}
    </div>

    <div class="sp-card">
      <div class="sp-card-head-row">
        <h3 class="sp-card-title">Options</h3>
        <button class="sp-btn-mini" @click="onAddOption">
          <i class="fa fa-plus"></i> Add option
        </button>
      </div>

      <div v-if="local.options.length === 0" class="sp-empty">
        No payment options configured yet. Add one to start.
      </div>

      <div
        v-for="(opt, idx) in local.options"
        :key="opt.id"
        class="sp-option"
        :class="{ disabled: !opt.enabled }"
      >
        <div class="sp-option-head">
          <!-- <label> wrapper forwards slider clicks to the hidden input;
               without it the absolute-positioned slider absorbs the click. -->
          <label class="sp-switch">
            <input type="checkbox" v-model="opt.enabled" />
            <span class="sp-switch-slider"></span>
          </label>
          <input
            v-model="opt.title"
            class="sp-input sp-input-title"
            placeholder="Option title (e.g., Pay in full)"
            maxlength="120"
          />
          <select v-model="opt.type" class="sp-select" @change="onTypeChange(opt)">
            <option value="discount">Discount</option>
            <option value="split">Split payment</option>
            <option value="inhouse">In-house plan</option>
            <option value="finance">Financing</option>
          </select>
          <label class="sp-rec">
            <input type="checkbox" :checked="opt.recommended" @change="onToggleRecommended(idx)" />
            <span>Most popular</span>
          </label>
          <button class="sp-icon-btn sp-icon-btn-danger" @click="onDeleteOption(idx)" title="Remove">
            <i class="fa fa-trash"></i>
          </button>
        </div>

        <div class="sp-option-body">
          <!-- Which lender actually backs this option - FIRST in the card, because it decides what
               everything below means: with None, the fields below are the practice's own
               hand-typed estimate; with Cherry, patients see Cherry's REAL plans for their amount
               instead (and no figures at all when Cherry can't price it - never the typed terms),
               and the typed tier inputs give way to the plans table. It used to sit at the
               bottom of the card and was easy to miss.
               Cherry is only selectable once it is connected AND a location is chosen — an option
               pointed at a half-finished connection would just fail at the chairside. An option
               ALREADY set to Cherry keeps the dropdown so it can always be set back to None. -->
          <div
            v-if="opt.type === 'finance' && (cherryEnabled || opt.provider === 'cherry')"
            class="sp-field sp-partner"
            data-test="financing-partner"
          >
            <label>Financing partner <span class="sp-paytype-optional">(optional)</span></label>
            <select v-model="opt.provider" class="sp-input" :disabled="!cherryUsable && opt.provider !== 'cherry'">
              <option :value="null">— None (estimated terms only) —</option>
              <option value="cherry" :disabled="!cherryUsable">Cherry</option>
            </select>
            <p v-if="opt.provider === 'cherry' && !cherryUsable" class="sp-warn-inline">
              This option is set to Cherry, but Cherry isn't connected right now, so its plans can't
              be refreshed — patients see plans fetched earlier, or no financing figures. Set it back
              to None to show your estimated terms.
            </p>
            <p v-if="!cherryState.connected && opt.provider !== 'cherry'" class="sp-hint">
              Connect a Cherry account above to back this option with real financing.
            </p>
            <p v-else-if="!cherryUsable && opt.provider !== 'cherry'" class="sp-hint">
              Choose a Cherry practice location above before assigning an option to Cherry.
            </p>
            <p v-else-if="opt.provider === 'cherry' && cherryUsable" class="sp-hint">
              Backed by <strong>{{ cherryState.businessName }}</strong> in Cherry. Patients see
              Cherry's real terms for their amount — or no figures at all when Cherry can't price it.
              The estimated terms you typed are not used while this option is on Cherry.
            </p>
            <p v-else-if="opt.provider !== 'cherry'" class="sp-hint">
              Leave as None to keep showing the estimated terms you typed below.
            </p>
          </div>

          <div class="sp-field">
            <label>Description (patient-facing)</label>
            <textarea v-model="opt.desc" class="sp-input sp-textarea" rows="2" maxlength="600" placeholder="Short description shown on the option card"></textarea>
            <!-- A Cherry-backed option never prints typed financing figures, so a description
                 built around them is withheld from patients entirely (see
                 interpolatePaymentDesc). Warn here, where it can be fixed. -->
            <div v-if="cherryHidesDesc(opt)" class="sp-warn" role="alert">
              <i class="fa fa-exclamation-triangle"></i>
              This description uses <strong>{months}</strong> or <strong>{apr}</strong>. Because this option
              is backed by Cherry, those would show your typed estimates instead of Cherry's real terms, so
              patients will see <em>no description</em> until you remove them.
            </div>
            <div v-if="tokensForOption(opt).length > 0" class="sp-token-hint">
              <span class="sp-token-hint-label">Insert a token (auto-fills the live value):</span>
              <button
                v-for="tok in tokensForOption(opt)"
                :key="tok"
                type="button"
                class="sp-token-chip"
                @click="insertToken(opt, tok)"
              >{{ '{' + tok + '}' }}</button>
            </div>
            <div v-if="hasTokens(opt.desc) && previewDesc(opt)" class="sp-token-preview">
              <i class="fa fa-eye"></i> {{ previewDesc(opt) }}
            </div>
          </div>
          <div class="sp-field">
            <label>Terms / conditions (patient-facing)</label>
            <input v-model="opt.terms" class="sp-input" maxlength="600" placeholder="Optional terms shown below the card" />
          </div>

          <div class="sp-config-row">
            <template v-if="opt.type === 'discount'">
              <label class="sp-config-label">Discount</label>
              <input type="number" v-model.number="opt.discount" class="sp-input sp-input-num" min="0" max="100" />
              <span class="sp-input-suffix">%</span>
            </template>
            <template v-else-if="opt.type === 'split'">
              <label class="sp-config-label">Splits</label>
              <input type="number" v-model.number="opt.splits" class="sp-input sp-input-num" min="2" max="6" />
              <span class="sp-input-suffix">payments</span>
            </template>
            <template v-else-if="opt.type === 'inhouse'">
              <label class="sp-config-label">Payments</label>
              <input type="number" v-model.number="opt.splits" class="sp-input sp-input-num" min="2" max="12" />
            </template>
            <template v-else-if="opt.type === 'finance'">
              <!-- The practice's own typed estimate. Hidden for a Cherry-backed option: patients
                   see Cherry's real terms, so these would be inputs that change nothing. -->
              <div v-if="opt.provider !== 'cherry'" class="sp-finance">
                <!-- Deferred Interest durations (estimated at 0% over the term) -->
                <div class="sp-finance-group">
                  <label class="sp-config-label">
                    Deferred Interest
                    <span class="sp-finance-grouphint">0% if paid in promo</span>
                  </label>
                  <div class="sp-tier-row">
                    <span v-for="(m, i) in (opt.deferredMonths || [])" :key="'d' + i" class="sp-tier-chip">
                      <input
                        type="number" class="sp-tier-input" min="1" max="120"
                        :value="m" @input="updateTier(opt, 'deferredMonths', i, $event.target.value)"
                      />
                      <span class="sp-tier-suffix">mo</span>
                      <button type="button" class="sp-tier-x" title="Remove" @click="removeTier(opt, 'deferredMonths', i)">×</button>
                    </span>
                    <button type="button" class="sp-tier-add" @click="addTier(opt, 'deferredMonths')">
                      <i class="fa fa-plus"></i> Add
                    </button>
                  </div>
                </div>

                <!-- Reduced APR durations (one shared APR applied to all) -->
                <div class="sp-finance-group">
                  <label class="sp-config-label">
                    Reduced APR
                    <span class="sp-finance-aprwrap">
                      @ <input type="number" v-model.number="opt.apr" class="sp-input sp-input-num sp-input-apr" step="0.1" min="0" max="36" /> %
                    </span>
                  </label>
                  <div class="sp-tier-row">
                    <span v-for="(m, i) in (opt.aprMonths || [])" :key="'a' + i" class="sp-tier-chip">
                      <input
                        type="number" class="sp-tier-input" min="1" max="120"
                        :value="m" @input="updateTier(opt, 'aprMonths', i, $event.target.value)"
                      />
                      <span class="sp-tier-suffix">mo</span>
                      <button type="button" class="sp-tier-x" title="Remove" @click="removeTier(opt, 'aprMonths', i)">×</button>
                    </span>
                    <button type="button" class="sp-tier-add" @click="addTier(opt, 'aprMonths')">
                      <i class="fa fa-plus"></i> Add
                    </button>
                  </div>
                </div>

                <p class="sp-hint">
                  Each duration shows the patient an <strong>estimated monthly payment</strong>,
                  calculated per patient from their portion. Deferred Interest is estimated at 0%
                  over the term; Reduced APR uses the APR above. Leave a group empty to omit it.
                </p>
              </div>

              <!-- Cherry-backed: the practice's REAL Cherry plans, with a switch per plan. The
                   rows come from the plan CATALOG (cherry-service prices a ladder of amounts -
                   Cherry has no list-my-plans endpoint). Unticking a plan only changes what
                   Teamio shows: it goes in this option's `cherryHiddenPlans`, and a plan Cherry
                   adds later appears here ticked, i.e. offered. -->
              <div v-else class="sp-finance sp-cherry-plans" data-test="cherry-plans">
                <div class="sp-cherry-head">
                  <label class="sp-config-label">Your Cherry plans</label>
                  <span v-if="catalog && catalogUpdatedText" class="sp-cherry-updated" data-test="cherry-catalog-updated">
                    Updated {{ catalogUpdatedText }}
                  </span>
                  <button
                    type="button"
                    class="sp-btn-mini sp-cherry-refresh"
                    data-test="cherry-catalog-refresh"
                    :disabled="catalogLoading || !cherryUsable"
                    @click="refreshCatalog()"
                  >
                    <i class="fa" :class="catalogLoading ? 'fa-circle-o-notch fa-spin' : 'fa-refresh'"></i>
                    {{ catalog ? 'Refresh from Cherry' : 'Load my Cherry plans' }}
                  </button>
                </div>

                <p v-if="catalogError" class="sp-warn-inline" role="alert" data-test="cherry-catalog-error">{{ catalogError }}</p>

                <p v-if="!catalog" class="sp-hint" data-test="cherry-catalog-empty">
                  <template v-if="cherryUsable">
                    Load your plans to see exactly what Cherry offers your practice and switch individual
                    plans off. Until then patients see every plan Cherry offers for their amount.
                  </template>
                  <template v-else>
                    Connect Cherry and choose a practice location above to see your plans here.
                  </template>
                </p>
                <p v-else-if="catalogRowsFor(opt).length === 0" class="sp-hint" data-test="cherry-catalog-none">
                  Cherry didn't return any plans for your account. Check your plan setup in the Cherry
                  portal, then refresh.
                </p>
                <template v-else>
                  <div class="sp-cherry-tablewrap">
                    <table class="sp-cherry-table" data-test="cherry-plans-table">
                      <thead>
                        <tr>
                          <!-- The switch column comes FIRST: at tablet width the table scrolls sideways
                               and a last-column checkbox ended up off-screen. -->
                          <th scope="col" class="sp-cherry-offer" title="Offer to patients">Offer</th>
                          <th scope="col">Plan</th>
                          <th scope="col">APR</th>
                          <th scope="col">Availability</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="row in catalogRowsFor(opt)"
                          :key="row.key"
                          :class="{ 'sp-cherry-off': !row.offered }"
                          data-test="cherry-plan-row"
                        >
                          <td class="sp-cherry-offer">
                            <input
                              type="checkbox"
                              :checked="row.offered"
                              :aria-label="'Offer ' + row.title + ' to patients'"
                              data-test="cherry-plan-offer"
                              @change="onToggleCherryPlan(opt, row.key, $event.target.checked)"
                            />
                          </td>
                          <td class="sp-cherry-plan">{{ row.title }}</td>
                          <td class="sp-cherry-apr">{{ row.apr }}</td>
                          <td class="sp-cherry-avail">{{ row.availability }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p v-if="catalogAllHidden(opt)" class="sp-warn" role="alert" data-test="cherry-all-hidden">
                    <i class="fa fa-exclamation-triangle"></i>
                    Every plan is switched off, so patients will see
                    "{{ noTermsText }}" for this option.
                  </p>
                </template>

                <p class="sp-hint" data-test="cherry-hide-note">
                  Switching a plan off only changes what Teamio shows. When a patient applies, Cherry can
                  still offer every plan on your account — to stop offering a plan altogether, turn it off in
                  your Cherry portal. Availability is approximate (Cherry's plans depend on the amount);
                  the Builder prices each patient's real amount.
                </p>
              </div>
            </template>
          </div>

          <!-- Flexibility toggles — what Naveen's Flex doesn't let an office do.
               Both default off; turning either on just enables an input in the
               Builder / Present Mode (the actual amounts are per-patient). -->
          <div class="sp-flex-section">
            <label class="sp-flex-check" :class="{ 'sp-flex-check-disabled': opt.type === 'discount' }">
              <input
                type="checkbox"
                v-model="opt.downpaymentEnabled"
                :disabled="opt.type === 'discount'"
              />
              <span class="sp-flex-text">
                <strong>Add "Pay today" downpayment option</strong>
                <span class="sp-flex-sub">
                  Chairside the TC enters a downpayment that subtracts from the
                  split/finance math (e.g., $500 today, then 2 × the remainder).
                  <template v-if="opt.type === 'discount'">Not applicable to Pay-in-full options.</template>
                </span>
              </span>
            </label>

            <label class="sp-flex-check">
              <input type="checkbox" v-model="opt.feeEnabled" />
              <span class="sp-flex-text">
                <strong>Add additional fee</strong>
                <span class="sp-flex-sub">
                  e.g., a card processing fee or financing fee. The amount is set
                  per-patient in the Builder; shown read-only on the chairside card
                  and added to the total.
                </span>
              </span>
            </label>

            <div v-if="opt.feeEnabled" class="sp-flex-detail">
              <div class="sp-field sp-field-inline">
                <label>Fee description (patient-facing)</label>
                <input
                  v-model="opt.feeDesc"
                  class="sp-input"
                  maxlength="120"
                  placeholder="e.g., Card processing fee, Financing Fee"
                />
              </div>
              <div class="sp-field sp-field-inline sp-field-unit">
                <label>Unit</label>
                <select v-model="opt.feeUnit" class="sp-select">
                  <option value="$">$ (flat amount)</option>
                  <option value="%">% (of patient portion)</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Open Dental PayType — drives the chairside "Mark as Financed
               via {Option}" deposit method on financing-type options. Each
               3rd-party financing partner (CareCredit, Cherry, Wisetack, …)
               gets its own Cat-10 PayType in OD, so this mapping lives
               per-option rather than as a global setting. -->
          <div v-if="opt.type === 'finance'" class="sp-paytype-section">
            <div class="sp-field">
              <label>Open Dental PayType <span class="sp-paytype-optional">(optional)</span></label>
              <select v-model.number="opt.odPayTypeDefNum" class="sp-input" :disabled="payTypesLoading">
                <option :value="null">— None (Mark as Financed via {{ opt.title || 'option' }} disabled chairside) —</option>
                <option v-for="p in payTypes" :key="p.defNum" :value="p.defNum">{{ p.itemName }}</option>
              </select>
              <p class="sp-hint">
                Set this for each 3rd-party financing partner you offer (CareCredit, Cherry, Wisetack, …) —
                each gets its own Cat-10 PayType. When the chairside deposit panel marks a deposit as
                "Financed via {{ opt.title || 'this option' }}", Open Dental records the payment with the
                PayType selected here. Leave as None if the office doesn't track this partner separately
                in OD (patients can still pick the option; deposit just routes through card / cash / check).
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="sp-savebar">
      <span v-if="multipleCherryMessage" class="sp-blocked" role="alert">{{ multipleCherryMessage }}</span>
      <span v-else-if="dirty" class="sp-dirty">Unsaved changes</span>
      <span v-else class="sp-clean">All changes saved</span>
      <span class="sp-spacer"></span>
      <button class="sp-btn" @click="onReset" :disabled="!dirty">Reset</button>
      <button class="sp-btn-primary" @click="onSave" :disabled="!dirty || saving || !!multipleCherryMessage">
        <i v-if="saving" class="fa fa-circle-o-notch fa-spin"></i>
        Save
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import mixin from '@/mixin.js';
import { TX_PRESENTER_DEFAULTS } from '@/pages/TxPresenter/constants/settingsDefaults.js';
import { interpolatePaymentDesc, descUsesFinanceTokens } from '@/pages/TxPresenter/utils/paymentOptions.js';
import { normalizeHiddenPlanKeys, cherryAsOfText, CHERRY_NO_TERMS_TEXT } from '@/pages/TxPresenter/utils/cherryTerms.js';
import { cherryCatalogRows, toggleHiddenPlanKey, isCatalogStale } from '@/pages/TxPresenter/utils/cherryCatalog.js';
import { extractApiError } from '@/utils/apiError';
import CherryConnectCard from './CherryConnectCard.vue';

// Which tokens make sense per option type — drives the contextual chips
// under the description editor. discount → {discount}; split/inhouse →
// {splits}; finance → {months}+{apr}.
const TOKENS_BY_TYPE = {
  discount: ['discount'],
  split: ['splits'],
  inhouse: ['splits'],
  // Multi-tier financing has no single term; the tier table carries the
  // durations. {apr} (the shared reduced-APR rate) is still useful in prose.
  finance: ['apr'],
};

// Starting value for a freshly added chip in each tier group.
const TIER_SEEDS = { deferredMonths: 6, aprMonths: 24 };

const cloneDefaults = () => JSON.parse(JSON.stringify(TX_PRESENTER_DEFAULTS.payment));

export default {
  name: 'SettingsPaymentOptions',
  mixins: [mixin],
  components: { CherryConnectCard },
  props: {
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      local: cloneDefaults(),
      original: cloneDefaults(),
      // OD Cat-10 PayTypes for the per-option dropdown — drives the
      // "Mark as Paid via {Option}" chairside CTA. Sourced from the same
      // /od-payment-definitions endpoint Settings → Payments uses.
      payTypes: [],
      payTypesLoading: false,
      // Reported up by CherryConnectCard. Kept OUT of `local` on purpose: the
      // connection is not part of the payment settings blob and must never end up
      // in a saved section, or a Save here would try to write Cherry state through
      // /save-tx-settings. `catalog` is the plan catalog ({ plans, ladder, fetchedAt } | null) -
      // display data owned by cherry-service, refreshed by the button below, never saved here.
      cherryState: { connected: false, usable: false, businessName: '', practiceName: '', catalog: null },
      // Set when Cherry stops being usable and options were switched back to None in the
      // (still unsaved) form. Cleared on Save / Reset.
      cherryNotice: '',
      // Plan-catalog refresh (12 GETs to Cherry): in flight, last error, and whether the
      // open-time auto-refresh has already had its one try (so a failure can't loop).
      catalogLoading: false,
      catalogError: '',
      catalogAutoTried: false,
    };
  },
  computed: {
    ...mapGetters('txPresenter', ['settingsPaymentResolved', 'cherryEnabled']),
    dirty() {
      return JSON.stringify(this.local) !== JSON.stringify(this.original);
    },
    /** A finance option can only be pointed at Cherry once there's a key AND a location. */
    cherryUsable() {
      return !!this.cherryState.usable;
    },
    /** What Cherry offers this practice: { plans, ladder, fetchedAt }, or null when never loaded. */
    catalog() {
      return this.cherryState.catalog || null;
    },
    /** Site timezone (GENERAL_TIMEZONE), same accessor CherryConnectCard uses. */
    siteTimezone() {
      return (this.allSettings
        && this.allSettings.GENERAL_TIMEZONE
        && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || 'America/New_York';
    },
    /** "October 10, 2026 at 11:00 AM" in the SITE's timezone ('' when the catalog is undated). */
    catalogUpdatedText() {
      return cherryAsOfText(this.catalog, this.siteTimezone);
    },
    /** The sentence patients see when no plan is left to show - quoted in the all-off warning. */
    noTermsText() {
      return CHERRY_NO_TERMS_TEXT;
    },
    /**
     * Cherry pricing is stored once per plan and read back for ONE option, so two Cherry-backed
     * options would fight over it (the second silently showing the first's terms, priced for a
     * different amount). Blocks Save with a message that names them; '' when fine.
     */
    multipleCherryMessage() {
      const backed = (this.local.options || []).filter((o) => o.provider === 'cherry');
      if (backed.length < 2) return '';
      const names = backed.map((o) => '"' + (o.title || 'Untitled option') + '"').join(', ');
      return 'Only one payment option can be backed by Cherry (' + names + '). Set the others to None to save.';
    },
  },
  mounted() {
    this.loadPayTypes();
  },
  watch: {
    settingsPaymentResolved: {
      immediate: true,
      handler(resolved) {
        if (!resolved) return;
        const merged = JSON.parse(JSON.stringify(resolved));
        // Backfill new per-option flexibility fields so v-model bindings
        // have something to bind to on options that were saved before these
        // toggles existed. Defaults match the type-level defaults.
        (merged.options || []).forEach((o) => {
          if (o.downpaymentEnabled === undefined) o.downpaymentEnabled = false;
          if (o.feeEnabled === undefined) o.feeEnabled = false;
          if (o.feeDesc === undefined) o.feeDesc = '';
          if (o.feeUnit === undefined) o.feeUnit = '$';
          // odPayTypeDefNum drives the chairside "Mark as Paid via {Option}"
          // CTA. null = not configured (CTA disabled). Set per option in the
          // dropdown below — required for 3rd-party financing partners.
          if (o.odPayTypeDefNum === undefined) o.odPayTypeDefNum = null;
          // provider names the third-party lender behind a finance option
          // (null | 'cherry'). Backfilled the same way and for the same reason as
          // odPayTypeDefNum: v-model needs something to bind to on options saved
          // before the field existed, and no blob migration is then required.
          if (o.provider === undefined) o.provider = null;
          // Multi-tier financing backfill: pre-existing single-term options
          // (just months+apr) become a single tier in the matching group, so
          // the two-group editor populates correctly with no visual regression.
          if (o.type === 'finance') {
            if (!Array.isArray(o.deferredMonths) && !Array.isArray(o.aprMonths)) {
              const m = parseInt(o.months, 10);
              const apr = parseFloat(o.apr) || 0;
              o.deferredMonths = (Number.isFinite(m) && apr <= 0) ? [m] : [];
              o.aprMonths = (Number.isFinite(m) && apr > 0) ? [m] : [];
            }
            if (!Array.isArray(o.deferredMonths)) o.deferredMonths = [];
            if (!Array.isArray(o.aprMonths)) o.aprMonths = [];
            if (typeof o.apr !== 'number') o.apr = parseFloat(o.apr) || 0;
          }
        });
        this.local = merged;
        this.original = JSON.parse(JSON.stringify(merged));
      },
    },
  },
  methods: {
    // ─── Description tokens ───────────────────────────
    // Returns the tokens that should appear as insert-chips under this option's
    // description. Base set is per type (discount → {discount}, etc.); {fee}
    // and {downpayment} appear only when their toggles are on.
    tokensForOption(opt) {
      // A Cherry-backed option never prints typed financing figures, so don't offer {apr}.
      const cherryBacked = !!opt && opt.type === 'finance' && opt.provider === 'cherry';
      const base = cherryBacked ? [] : (TOKENS_BY_TYPE[opt && opt.type] || []);
      const extras = [];
      if (opt && opt.feeEnabled) extras.push('fee');
      if (opt && opt.downpaymentEnabled && opt.type !== 'discount') extras.push('downpayment');
      return [...base, ...extras];
    },
    hasTokens(desc) {
      return typeof desc === 'string' && /\{\w+\}/.test(desc);
    },
    /** Will patients lose this option's description? (Cherry-backed + a typed-figure token.) */
    cherryHidesDesc(opt) {
      return !!opt && opt.type === 'finance' && opt.provider === 'cherry' && descUsesFinanceTokens(opt.desc);
    },
    previewDesc(opt) {
      return interpolatePaymentDesc(opt);
    },
    insertToken(opt, tok) {
      const token = `{${tok}}`;
      const cur = opt.desc || '';
      // Append with a single separating space when the field already has text.
      opt.desc = cur && !/\s$/.test(cur) ? `${cur} ${token}` : `${cur}${token}`;
    },

    // ─── Finance tiers (multi-tier financing) ─────────
    // Ensure a finance option carries the multi-tier fields so v-model/array
    // bindings have something to bind to (Vue 2 reactivity needs $set for
    // freshly-added keys).
    ensureFinanceArrays(opt) {
      if (!Array.isArray(opt.deferredMonths)) this.$set(opt, 'deferredMonths', []);
      if (!Array.isArray(opt.aprMonths)) this.$set(opt, 'aprMonths', []);
      if (typeof opt.apr !== 'number') this.$set(opt, 'apr', parseFloat(opt.apr) || 0);
    },
    // When an option is switched TO financing, seed the tier fields (backfilling
    // any legacy months/apr into the matching group) so the editor isn't blank.
    onTypeChange(opt) {
      // provider only means something on a finance option; don't leave a phantom Cherry link on
      // a discount/split one (it would still count toward the one-Cherry-option rule).
      if (opt.type !== 'finance' && opt.provider === 'cherry') opt.provider = null;
      if (opt.type !== 'finance') return;
      if (!Array.isArray(opt.deferredMonths) && !Array.isArray(opt.aprMonths)) {
        const m = parseInt(opt.months, 10);
        const apr = parseFloat(opt.apr) || 0;
        this.$set(opt, 'deferredMonths', (Number.isFinite(m) && apr <= 0) ? [m] : []);
        this.$set(opt, 'aprMonths', (Number.isFinite(m) && apr > 0) ? [m] : []);
      }
      this.ensureFinanceArrays(opt);
      if (opt.odPayTypeDefNum === undefined) this.$set(opt, 'odPayTypeDefNum', null);
    },
    addTier(opt, group) {
      this.ensureFinanceArrays(opt);
      if (!Array.isArray(opt[group])) this.$set(opt, group, []);
      opt[group].push(TIER_SEEDS[group] || 24);
    },
    removeTier(opt, group, i) {
      if (Array.isArray(opt[group])) opt[group].splice(i, 1);
    },
    updateTier(opt, group, i, val) {
      this.ensureFinanceArrays(opt);
      if (!Array.isArray(opt[group])) this.$set(opt, group, []);
      const n = parseInt(val, 10);
      // Keep '' while the field is being cleared/edited; sanitized on save.
      this.$set(opt[group], i, (Number.isFinite(n) && n > 0) ? n : '');
    },
    // Drop empty/invalid tier durations and coerce APR before persisting, so
    // the stored blob is clean (the calc tolerates junk, but we don't store it).
    sanitizeFinanceInPlace() {
      const clean = (arr) => (Array.isArray(arr)
        ? arr.map((m) => parseInt(m, 10)).filter((m) => Number.isFinite(m) && m > 0)
        : []);
      (this.local.options || []).forEach((o) => {
        if (o.type === 'finance') {
          o.deferredMonths = clean(o.deferredMonths);
          o.aprMonths = clean(o.aprMonths);
          o.apr = Math.max(0, parseFloat(o.apr) || 0);
          // A Cherry option always carries its hide list explicitly (even as []), so an emptied
          // list overwrites a per-patient snapshot's stale one - overlayRecommendedFromSettings
          // only copies keys the live option actually has. Keys are strings, trimmed, upper-cased
          // and de-duplicated (the shape cherryPlansFor matches). The legacy `cherryTermMonths`
          // allow-list is dropped: the plans table replaced it, and no site stored one. Other
          // options are left untouched: absent is a valid value and must not dirty existing blobs.
          if (o.provider === 'cherry') {
            o.cherryHiddenPlans = normalizeHiddenPlanKeys(o.cherryHiddenPlans);
            this.$delete(o, 'cherryTermMonths');
          }
        }
      });
    },

    onAddOption() {
      const id = 'opt_' + Date.now();
      this.local.options.push({
        id,
        title: 'New option',
        type: 'discount',
        desc: '',
        enabled: true,
        recommended: false,
        discount: 0,
        terms: '',
        downpaymentEnabled: false,
        feeEnabled: false,
        feeDesc: '',
        feeUnit: '$',
        odPayTypeDefNum: null,
        provider: null,
      });
    },

    /**
     * CherryConnectCard reports its state up so the per-option "Financing partner"
     * dropdown knows whether Cherry is actually selectable.
     *
     * When Cherry goes from usable to NOT usable (disconnected, or the location removed) the
     * options backed by it are switched back to None and the office is told. This replaces the
     * old "keep pointing at Cherry and fall back to the typed terms" behaviour: there is no
     * typed-terms fallback any more - a Cherry-backed option with no connection shows patients
     * no figures at all - so leaving the link in place would silently blank the financing card.
     *
     * Only the usable -> unusable TRANSITION counts. The card also reports "not connected" for a
     * failed load on mount, and that must never rewrite the form. The change is made to the
     * unsaved form, not saved: the office confirms it with Save (an office rotating a key can
     * Reset and reconnect instead).
     */
    onCherryChange(state) {
      const wasUsable = this.cherryState.usable;
      this.cherryState = { ...this.cherryState, ...(state || {}) };
      if (wasUsable && !this.cherryState.usable) this.unlinkCherryOptions();
      this.maybeAutoRefreshCatalog();
    },

    // ─── Your Cherry plans (the catalog) ──────────────
    /** The table rows for an option: catalog plans, each `offered` unless in opt.cherryHiddenPlans. */
    catalogRowsFor(opt) {
      return cherryCatalogRows(this.catalog, opt && opt.cherryHiddenPlans);
    },
    /** Is every plan in the catalog switched off for this option? (Patients then see no figures.) */
    catalogAllHidden(opt) {
      const rows = this.catalogRowsFor(opt);
      return rows.length > 0 && rows.every((r) => !r.offered);
    },
    /** The "Offer to patients" checkbox: unticked puts the plan's key in the option's hide list. */
    onToggleCherryPlan(opt, key, offered) {
      // $set: an option saved before this feature has no `cherryHiddenPlans` key to be reactive.
      this.$set(opt, 'cherryHiddenPlans', toggleHiddenPlanKey(opt.cherryHiddenPlans, key, offered));
    },
    /**
     * Ask cherry-service to rebuild the catalog (12 priced amounts - Cherry has no list-my-plans
     * endpoint). On success the new catalog replaces the one from get-connection; on failure the
     * old one stays and the reason is shown. Only ever called from the button or the one open-time
     * auto-refresh below - never from a render path.
     */
    async refreshCatalog() {
      if (this.catalogLoading || !this.siteID || !this.cherryUsable) return;
      this.catalogLoading = true;
      this.catalogError = '';
      try {
        const catalog = await this.$store.dispatch('txPresenter/fetchCherryPlanCatalog', { siteID: this.siteID });
        this.cherryState = {
          ...this.cherryState,
          catalog: catalog && typeof catalog === 'object' ? catalog : null,
        };
      } catch (err) {
        this.catalogError = extractApiError(err, "Couldn't refresh your Cherry plans. Try again in a moment.");
      } finally {
        this.catalogLoading = false;
      }
    },
    /**
     * Settings just opened on a connection whose catalog is more than a week old -> refresh it once,
     * quietly. A catalog that was never loaded is NOT auto-loaded: that is the "Load my Cherry
     * plans" button (12 requests to Cherry on the practice's account is the office's call), and a
     * fresh one needs nothing. `catalogAutoTried` makes it one attempt per visit, so a failing
     * refresh can never loop.
     */
    maybeAutoRefreshCatalog() {
      if (this.catalogAutoTried || !this.cherryUsable || !this.catalog) return;
      if (!isCatalogStale(this.catalog)) return;
      this.catalogAutoTried = true;
      this.refreshCatalog();
    },
    unlinkCherryOptions() {
      const names = [];
      (this.local.options || []).forEach((o) => {
        if (o.provider === 'cherry') {
          o.provider = null;
          names.push('"' + (o.title || 'Untitled option') + '"');
        }
      });
      if (!names.length) return;
      this.cherryNotice = 'Cherry is no longer connected, so ' + names.join(', ')
        + (names.length === 1 ? ' was' : ' were') + ' set back to None (estimated terms only). '
        + 'Click Save to apply this; until you do, patients may still see Cherry plans that were '
        + 'fetched earlier, or no financing figures.';
    },

    // Load OD Cat-10 PayTypes so each option card can pick which one
    // a "Mark as Paid via {Option}" chairside deposit lands as. Same
    // endpoint Settings → Payments uses for the card-brand mapping.
    async loadPayTypes() {
      if (!this.siteID) return;
      this.payTypesLoading = true;
      try {
        const res = await this.$store.dispatch('teamio/getOdPaymentDefinitions', this.siteID);
        // Endpoint returns DefNum + ItemName; accept either casing for resilience.
        const list = (res && (res.payTypes || res.PayTypes)) || [];
        this.payTypes = list.map((p) => ({
          defNum: p.DefNum != null ? p.DefNum : p.defNum,
          itemName: p.ItemName != null ? p.ItemName : p.itemName,
        })).filter((p) => p.defNum != null);
      } catch (e) {
        console.warn('[SettingsPaymentOptions] failed to load OD PayTypes:', e.message);
        this.payTypes = [];
      } finally {
        this.payTypesLoading = false;
      }
    },
    onDeleteOption(idx) {
      if (!window.confirm('Remove this payment option from the practice configuration?')) return;
      this.local.options.splice(idx, 1);
    },
    onToggleRecommended(idx) {
      // Only one option can be marked "most popular" — flip the rest off
      this.local.options.forEach((o, i) => {
        o.recommended = i === idx ? !o.recommended : false;
      });
    },
    async onSave() {
      // The button is disabled in this state; this is the belt to its braces.
      if (this.multipleCherryMessage) return;
      this.sanitizeFinanceInPlace();
      try {
        await this.$store.dispatch('txPresenter/saveSettingsSection', {
          section: 'payment',
          value: this.local,
        });
        this.original = JSON.parse(JSON.stringify(this.local));
        this.cherryNotice = '';
      } catch (e) { /* in state */ }
    },
    onReset() {
      this.local = JSON.parse(JSON.stringify(this.original));
      this.cherryNotice = '';
    },
  },
};
</script>

<style scoped>
.sp {
  max-width: 760px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.sp-section-head { margin-bottom: 22px; }
.sp-section-head h2 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: #0f172a;
}
.sp-help {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.55;
}

.sp-card {
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 20px 22px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(15,23,42,0.04);
}
.sp-card-title {
  margin: 0 0 14px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #475569;
}
.sp-card-head-row {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}
.sp-card-head-row .sp-card-title { margin: 0; }

.sp-field { margin-bottom: 14px; }
.sp-field > label {
  display: block;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  margin-bottom: 5px;
}

.sp-input,
.sp-textarea,
.sp-select,
.sp-input-num {
  display: block;
  width: 100%;
  padding: 7px 10px;
  font-size: 13px;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
}
.sp-textarea { resize: vertical; min-height: 56px; }

/* Description token chips + live preview */
.sp-token-hint {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
.sp-token-hint-label {
  font-size: 11px;
  color: #94a3b8;
}
.sp-token-chip {
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 11px;
  color: #0d5f54;
  background: #e6f1ef;
  border: 1px solid #b9dcd5;
  border-radius: 5px;
  padding: 2px 7px;
  cursor: pointer;
  transition: background 0.12s;
}
.sp-token-chip:hover { background: #d2e8e3; }
.sp-token-preview {
  margin-top: 6px;
  font-size: 12px;
  color: #475569;
  font-style: italic;
  background: #f8fafc;
  border-left: 2px solid #0d5f54;
  padding: 5px 10px;
  border-radius: 0 4px 4px 0;
}
.sp-token-preview .fa { color: #94a3b8; margin-right: 4px; font-style: normal; }
/* Amber callouts - a description that will be withheld, a Cherry link that was removed. */
.sp-warn,
.sp-notice {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: #78350f;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 7px 10px;
}
.sp-warn .fa,
.sp-notice .fa { margin-right: 4px; color: #b45309; }
.sp-notice { margin: 0 0 16px; }
.sp-warn-inline {
  margin: 6px 0 0;
  font-size: 11.5px;
  line-height: 1.5;
  color: #b45309;
}
.sp-input:focus,
.sp-textarea:focus,
.sp-select:focus,
.sp-input-num:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.sp-input-num {
  width: 88px;
  font-variant-numeric: tabular-nums;
}
.sp-input-title { flex: 1; }

/* Native select rendering was clipping the selected value (white-on-white
   inside the browser's own widget). Force a clean custom dropdown with our
   own chevron so the text always renders, regardless of OS/browser. */
.sp-select {
  width: 130px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  height: 34px;
  line-height: 1.4;
  padding: 0 28px 0 10px;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='none' stroke='%23475569' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M3 4.5l3 3 3-3'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px 12px;
  cursor: pointer;
}
/* Wider variant inside the flex-detail box (Unit selector — gives the
   "% (of patient portion)" option room without truncation). */
.sp-field-unit .sp-select { width: 100%; }

/* Same native-select fix as `.sp-select` above, but applied to ANY
   <select class="sp-input"> — without this, the browser's native widget
   renders the selected value white-on-white inside our custom-themed
   inputs and the picked value disappears. This recurs every time someone
   adds a new <select> with the default `sp-input` class (most recently
   the per-option OD PayType dropdown). Mirrors `select.sg-input` in
   SettingsGeneral.vue. */
select.sp-input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  height: 34px;
  line-height: 1.4;
  padding: 0 30px 0 10px;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='none' stroke='%23475569' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M3 4.5l3 3 3-3'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px 12px;
  cursor: pointer;
}
select.sp-input:disabled {
  background-color: #f1f3f6;
  color: #94a3b8;
  cursor: not-allowed;
}

/* Per-option OD PayType picker — separate band so it doesn't read as a
   sub-feature of the fee toggle above it. */
.sp-paytype-section {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #e4e7ec;
}
.sp-paytype-optional {
  font-weight: 400;
  color: #94a3b8;
  font-size: 11px;
  letter-spacing: 0;
  text-transform: none;
}

.sp-hint {
  margin: 6px 0 0;
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.5;
}

.sp-empty {
  padding: 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  border: 1px dashed #e4e7ec;
  border-radius: 8px;
}

.sp-option {
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  margin-bottom: 12px;
  background: #fafbfc;
  transition: opacity 0.15s;
}
.sp-option.disabled { opacity: 0.65; }
.sp-option-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid #e4e7ec;
}
.sp-option-body {
  padding: 14px;
}

.sp-rec {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  white-space: nowrap;
}
.sp-rec input[type="checkbox"] {
  accent-color: #d97706;
  margin: 0;
}

.sp-config-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.sp-config-label {
  font-size: 11px;
  color: #475569;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0;
}
.sp-config-label-2 { margin-left: 14px; }
.sp-input-suffix {
  font-size: 11px;
  color: #94a3b8;
}

/* Multi-tier financing editor — two duration groups (Deferred + Reduced APR). */
.sp-finance {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.sp-finance-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.sp-finance-group .sp-config-label {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sp-finance-grouphint {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  font-size: 11px;
  color: #94a3b8;
}
.sp-finance-aprwrap {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  font-size: 12px;
  color: #475569;
}
.sp-input-apr {
  width: 60px;
  padding: 4px 6px;
}
.sp-tier-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.sp-tier-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #fff;
  border: 1px solid #d8dee6;
  border-radius: 7px;
  padding: 3px 4px 3px 8px;
}
.sp-tier-input {
  width: 46px;
  border: none;
  padding: 2px 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  text-align: right;
  background: transparent;
  color: #0f172a;
}
.sp-tier-input:focus { outline: none; }
.sp-tier-suffix { font-size: 11px; color: #94a3b8; }
.sp-tier-x {
  border: none;
  background: transparent;
  color: #b0b8c4;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}
.sp-tier-x:hover { color: #b91c1c; }
.sp-tier-add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px dashed #b9dcd5;
  background: #f3faf8;
  color: #0d5f54;
  border-radius: 7px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
}
.sp-tier-add:hover { background: #e6f1ef; }

/* Financing partner leads the card: a quiet divider separates "who backs this" from the fields it
   governs. */
.sp-partner {
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px dashed #e4e7ec;
}

/* Your Cherry plans - the plan catalog with an "Offer to patients" switch per plan. */
.sp-cherry-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
}
.sp-cherry-updated {
  font-size: 11.5px;
  color: #94a3b8;
}
.sp-cherry-refresh { margin-left: auto; }
.sp-cherry-refresh:disabled { opacity: 0.55; cursor: not-allowed; }
/* The table scrolls sideways inside its own box on a phone instead of stretching the page. */
.sp-cherry-tablewrap {
  overflow-x: auto;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
}
.sp-cherry-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}
.sp-cherry-table th {
  padding: 7px 10px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #475569;
  background: #f7f8fa;
  border-bottom: 1px solid #e4e7ec;
  white-space: nowrap;
}
.sp-cherry-table td {
  padding: 7px 10px;
  border-bottom: 1px solid #f1f3f6;
  vertical-align: middle;
}
.sp-cherry-table tbody tr:last-child td { border-bottom: none; }
.sp-cherry-plan { font-weight: 600; white-space: nowrap; }
.sp-cherry-avail { color: #64748b; }
.sp-cherry-apr { white-space: nowrap; }
.sp-cherry-table .sp-cherry-offer { width: 1%; text-align: center; white-space: nowrap; }
.sp-cherry-offer input[type="checkbox"] {
  accent-color: #0d5f54;
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
}
/* A plan switched off stays readable but recedes, so the ones patients WILL see stand out. */
.sp-cherry-off .sp-cherry-plan,
.sp-cherry-off td { color: #94a3b8; }
.sp-cherry-off .sp-cherry-plan { text-decoration: line-through; }

/* Flexibility section — Pay today + Additional Fee toggles per option. */
.sp-flex-section {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #e4e7ec;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.sp-flex-check {
  display: flex !important;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  padding: 4px 0;
}
.sp-flex-check input[type="checkbox"] {
  margin-top: 2px;
  accent-color: #0d5f54;
  flex-shrink: 0;
  cursor: pointer;
}
.sp-flex-check-disabled { opacity: 0.55; cursor: not-allowed; }
.sp-flex-check-disabled input[type="checkbox"] { cursor: not-allowed; }
.sp-flex-text { display: flex; flex-direction: column; gap: 2px; }
.sp-flex-text strong { font-size: 13px; color: #0f172a; font-weight: 600; }
.sp-flex-sub {
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.5;
}
.sp-flex-detail {
  margin-left: 26px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 12px;
  background: #f7f8fa;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
}
.sp-field-inline { margin-bottom: 0; flex: 1 1 220px; min-width: 200px; }
.sp-field-unit { flex: 0 0 200px; }

.sp-icon-btn,
.sp-btn-mini {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e4e7ec;
  color: #475569;
  font-size: 12px;
  cursor: pointer;
}
.sp-icon-btn { padding: 5px 8px; }
.sp-icon-btn:hover { background: #f1f3f6; }
.sp-icon-btn-danger:hover { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
.sp-btn-mini:hover { background: #ecf5f3; color: #0d5f54; border-color: #b9dcd5; }

/* Toggle switch */
.sp-switch {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
  flex-shrink: 0;
}
/* Input fills the switch and sits transparently on top so it's the click
   target directly — no reliance on <label> click-forwarding. */
.sp-switch input[type="checkbox"] {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
}
.sp-switch-slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 18px;
  transition: background 0.18s;
  cursor: pointer;
}
.sp-switch-slider::before {
  content: '';
  position: absolute;
  height: 14px;
  width: 14px;
  left: 2px;
  top: 2px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.18s;
  box-shadow: 0 1px 2px rgba(15,23,42,0.12);
}
.sp-switch input:checked + .sp-switch-slider { background: #0d5f54; }
.sp-switch input:checked + .sp-switch-slider::before { transform: translateX(14px); }

/* Save bar */
.sp-savebar {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  margin-top: 24px;
  box-shadow: 0 4px 12px rgba(15,23,42,0.05);
}
.sp-dirty { font-size: 12px; color: #b45309; font-weight: 500; }
.sp-blocked { font-size: 12px; color: #b91c1c; font-weight: 500; line-height: 1.4; }
.sp-clean { font-size: 12px; color: #64748b; }
.sp-spacer { flex: 1; }
.sp-btn,
.sp-btn-primary {
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
}
.sp-btn:hover:not(:disabled) { background: #f1f3f6; }
.sp-btn:disabled,
.sp-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
.sp-btn-primary {
  background: #0d5f54;
  border-color: #0d5f54;
  color: #fff;
}
.sp-btn-primary:hover:not(:disabled) { background: #094840; }
</style>
